// LightboxGallery — Vignette + modale plein écran (zoom + navigation clavier)
// Usage :
//   <LightboxProvider>
//     <LightboxImage src="..." alt="..." testId="section-image-01" />
//     <LightboxImage src="..." alt="..." testId="section-image-02" />
//   </LightboxProvider>
// Chaque vignette s'auto-enregistre auprès du provider (ordre d'apparition).
// La modale gère : ESC (fermer), ← / → (navigation), + / - (zoom), 0 (reset).
// Zoom : molette (desktop) + pincement (mobile) + double clic / double tap.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useId,
  useMemo,
} from 'react';
import { ZoomIn, ZoomOut, X, ChevronLeft, ChevronRight, Maximize2, RotateCcw } from 'lucide-react';

const LightboxCtx = createContext(null);

export function LightboxProvider({ children }) {
  const [images, setImages] = useState([]); // [{ id, src, alt, caption }]
  const [openIndex, setOpenIndex] = useState(-1);

  const registerImage = useCallback((img) => {
    setImages((prev) => {
      const idx = prev.findIndex((p) => p.id === img.id);
      if (idx === -1) return [...prev, img];
      const next = [...prev];
      next[idx] = img;
      return next;
    });
  }, []);

  const unregisterImage = useCallback((id) => {
    setImages((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const openAt = useCallback((id) => {
    setImages((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx >= 0) setOpenIndex(idx);
      return prev;
    });
  }, []);

  const close = useCallback(() => setOpenIndex(-1), []);
  const next = useCallback(
    () => setOpenIndex((i) => (i < 0 ? -1 : Math.min(i + 1, images.length - 1))),
    [images.length]
  );
  const prev = useCallback(
    () => setOpenIndex((i) => (i < 0 ? -1 : Math.max(i - 1, 0))),
    []
  );

  const value = useMemo(
    () => ({ registerImage, unregisterImage, openAt, images, openIndex, close, next, prev }),
    [registerImage, unregisterImage, openAt, images, openIndex, close, next, prev]
  );

  return (
    <LightboxCtx.Provider value={value}>
      {children}
      {openIndex >= 0 && images[openIndex] && (
        <LightboxModal
          image={images[openIndex]}
          index={openIndex}
          total={images.length}
          onClose={close}
          onNext={next}
          onPrev={prev}
        />
      )}
    </LightboxCtx.Provider>
  );
}

export function LightboxImage({ src, alt, caption, testId, className = '' }) {
  const ctx = useContext(LightboxCtx);
  const rid = useId();
  const idRef = useRef(rid);

  useEffect(() => {
    if (!ctx) return;
    ctx.registerImage({ id: idRef.current, src, alt: alt || '', caption: caption || '' });
    return () => ctx.unregisterImage(idRef.current);
  }, [ctx, src, alt, caption]);

  const handleOpen = () => {
    if (ctx) ctx.openAt(idRef.current);
  };

  // Vignette : ratio 3:2, object-contain, hauteur maîtrisée, aucun rognage
  return (
    <button
      type="button"
      onClick={handleOpen}
      className={`group relative block w-full my-5 overflow-hidden rounded-xl border border-white/10 bg-black/60 shadow-lg transition hover:border-red-500/60 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
      style={{ aspectRatio: '3 / 2' }}
      aria-label={`Agrandir l'image : ${alt || 'illustration du cours'}`}
      data-testid={testId || 'lightbox-thumb'}
    >
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        className="w-full h-full object-contain select-none pointer-events-none"
        draggable="false"
      />
      {/* Overlay hint */}
      <span
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 backdrop-blur-sm border border-white/15 text-[10px] font-black uppercase tracking-widest text-white/90 opacity-90 group-hover:opacity-100 group-hover:bg-red-600/90 group-hover:border-red-500 transition"
        aria-hidden="true"
      >
        <Maximize2 className="w-3 h-3" /> Agrandir
      </span>
      {caption && (
        <span className="sr-only">{caption}</span>
      )}
    </button>
  );
}

/* ----------------- MODALE ----------------- */

function LightboxModal({ image, index, total, onClose, onNext, onPrev }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const containerRef = useRef(null);
  const draggingRef = useRef(null); // { startX, startY, baseTx, baseTy }
  const pinchRef = useRef(null); // { startDist, startScale }

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Reset au changement d'image
  useEffect(() => {
    reset();
  }, [image.src, reset]);

  // Bloque le scroll de la page
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Clavier
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === '+' || e.key === '=') setScale((s) => Math.min(s + 0.25, 4));
      else if (e.key === '-') setScale((s) => Math.max(s - 0.25, 1));
      else if (e.key === '0') reset();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, onNext, onPrev, reset]);

  // Molette = zoom (avec Ctrl aussi ok)
  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    setScale((s) => {
      const next = Math.min(Math.max(s + (delta > 0 ? 0.2 : -0.2), 1), 4);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return next;
    });
  };

  // Drag (souris + touch simple)
  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: tx,
      baseTy: ty,
    };
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const { startX, startY, baseTx, baseTy } = draggingRef.current;
    setTx(baseTx + (e.clientX - startX));
    setTy(baseTy + (e.clientY - startY));
  };
  const onPointerUp = (e) => {
    if (draggingRef.current) {
      draggingRef.current = null;
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) { /* noop */ }
  };

  // Pinch (deux doigts)
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { startDist: Math.hypot(dx, dy), startScale: scale };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchRef.current.startDist;
      const next = Math.min(Math.max(pinchRef.current.startScale * ratio, 1), 4);
      setScale(next);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      pinchRef.current = null;
    }
  };

  // Double clic = toggle zoom
  const onDoubleClick = () => {
    if (scale > 1) reset();
    else setScale(2);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse d'image"
      data-testid="lightbox-modal"
    >
      {/* Barre haut */}
      <div className="shrink-0 h-14 px-3 sm:px-5 flex items-center justify-between gap-3 border-b border-white/10 bg-black/60">
        <div className="flex items-center gap-2 text-white/85">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-md bg-red-600 text-white text-[11px] font-black uppercase tracking-widest"
            data-testid="lightbox-counter"
          >
            {index + 1} / {total}
          </span>
          {image.alt && (
            <span className="hidden sm:block text-[13px] text-white/70 truncate max-w-[50vw]">
              {image.alt}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setScale((s) => Math.max(s - 0.25, 1))}
            className="w-9 h-9 inline-flex items-center justify-center rounded-md bg-white/5 border border-white/10 text-white/90 hover:bg-white/15 transition disabled:opacity-30"
            disabled={scale <= 1}
            aria-label="Dézoomer"
            data-testid="lightbox-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span
            className="hidden sm:inline-block min-w-[46px] text-center text-[11px] font-black tracking-widest text-white/70 tabular-nums"
            data-testid="lightbox-zoom-level"
          >
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setScale((s) => Math.min(s + 0.25, 4))}
            className="w-9 h-9 inline-flex items-center justify-center rounded-md bg-white/5 border border-white/10 text-white/90 hover:bg-white/15 transition disabled:opacity-30"
            disabled={scale >= 4}
            aria-label="Zoomer"
            data-testid="lightbox-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={reset}
            className="w-9 h-9 inline-flex items-center justify-center rounded-md bg-white/5 border border-white/10 text-white/90 hover:bg-white/15 transition"
            aria-label="Réinitialiser le zoom"
            data-testid="lightbox-reset"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-1 w-9 h-9 inline-flex items-center justify-center rounded-md bg-red-600 hover:bg-red-500 text-white transition"
            aria-label="Fermer"
            data-testid="lightbox-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Zone image */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={onDoubleClick}
        style={{ cursor: scale > 1 ? (draggingRef.current ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        <img
          src={image.src}
          alt={image.alt || ''}
          className="absolute inset-0 m-auto max-w-full max-h-full select-none"
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: draggingRef.current ? 'none' : 'transform 120ms ease-out',
            userSelect: 'none',
            touchAction: 'none',
          }}
          draggable="false"
          data-testid="lightbox-image"
        />

        {/* Flèches nav */}
        <button
          type="button"
          onClick={onPrev}
          disabled={index === 0}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-red-600/90 border border-white/15 text-white flex items-center justify-center transition disabled:opacity-25 disabled:cursor-not-allowed"
          aria-label="Image précédente"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={index === total - 1}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-red-600/90 border border-white/15 text-white flex items-center justify-center transition disabled:opacity-25 disabled:cursor-not-allowed"
          aria-label="Image suivante"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Barre bas : hint clavier */}
      <div className="shrink-0 px-4 py-2 text-center text-[10px] sm:text-[11px] uppercase tracking-widest text-white/50 border-t border-white/10 bg-black/60">
        <span className="hidden sm:inline">← → naviguer · + / − zoomer · 0 reset · ESC fermer</span>
        <span className="sm:hidden">Pincer pour zoomer · Balayer pour naviguer</span>
      </div>
    </div>
  );
}

export default LightboxImage;
